<template>

	<div
		class="card"
		v-if="school.active"
		:class="{'isSelected': school.selected, 'disable-compare': school.disabled}"
		:aria-label="school.name">

		<p
			v-if="addressFilterActive"
			class="text-center p-2"
			:style="{ 'background-color': 'white', 'font-weight': 800 }">
			{{ school.distance }} miles away
		</p>

		<!-- School Name -->
		<div :class="{ 'card-header': true, 'disableClick' : school.disabled }" :style="{ 'background-color': school.color }" @click="toggleMoreModal">
			<img v-if="school.activeLogoImage" :src="`${school.imagesPath}/logos/${school.activeLogoImage.name}`" :alt="school.activeLogoImage.alt_text" :height="80" :width="80">
      <img v-else height="80px" width="80px" :src="`images/default/logo-placeholder.png`" :alt="'Placeholder logo image'">
      <img v-else height="80px" width="80px" :src="`/images/default/logo-placeholder.png`" :alt="'Placeholder logo image'">
			<h2 class="notranslate" :aria-label="school.name">{{ school.name }}</h2>
		</div>

		<!-- Compare Section -->
		<div class="compare-box" :style="{ 'background-color': lighterColor }">
			<label :class="{ addHover: !school.disabled }">
				Compare School
				<input
					type="checkbox"
					:class="{ addHover: !school.disabled }"
					:checked="school.selected"
					:disabled="school.disabled"
					@click="toggleCompare">
			</label>
		</div>

		<!-- Default Card View -->
		<div v-show="default_view" class="content default-view animated slow fadeIn">
			<div class="img-container">
				<img v-if="school.activeCardImage" :src="`${school.imagesPath}/cards/${school.activeCardImage.name}`" :alt="school.activeCardImage.alt_text" ref="schoolImg" tabindex="-1">
        <img v-else :src="`images/default/placeholder.png`" :alt="'Placeholder card image'" ref="schoolImg">
        <img v-else :src="`/images/default/placeholder.png`" :alt="'Placeholder card image'" ref="schoolImg">
			</div>
			<div class="quote-container" aria-label="A quote from the principal">
				<p>"{{school.principal_quote}}"</p>
				<span>
					{{school.principals[0]}}
					<br>
					<small>Principal</small>
				</span>
			</div>
		</div>

		<!-- Contact Info View -->
		<div v-show="contact_view" class="content contact-view animated slow fadeIn">
			<div class="section d-flex align-items-baseline">
				<i class="fas fa-globe-americas" :style="{ 'color': school.color }"></i>
				<a
          class="notranslate"
					:href="school.url" target="_blank"
					@click="triggerAnalytics('click_website_link')"
					v-text="school.url" ref="websiteLink">
				</a>
			</div>
			<div class="section d-flex align-items-baseline">
				<i class="fas fa-map-marker-alt" :style="{ 'color' : school.color, 'padding-left': '2px'}"></i>
				<a
          class="notranslate"
					:href="'https://maps.google.com/?q=' + school.address + ' ' + school.city + ' ' + school.zipCode"
					@click="triggerAnalytics('click_gmaps_link')"
					target="_blank">
					{{school.address}} <br/> {{school.city}}, AZ {{school.zipCode}}
				</a>
			</div>
			<div class="section">
				<i class="fas fa-phone" :style="{ 'color': school.color }"></i>
				<span v-if="isMobile">
					<a :href="'tel:+1' + school.officePhone" @click="triggerAnalytics('click_phone_number')">{{school.officePhone}}</a>
        </span>
				<span v-else>{{school.officePhone}}</span>
			</div>
			<hr>
			<div class="section" aria-label="school hours">
				<h3 :style="{ 'color': school.color }">School Hours</h3>
				<div class="d-flex justify-content-between align-items-center w-100">
					<span class="school-hours">Mon, Tue, Thu, Fri</span>
					<span class="school-hours">{{school.school_hours.mttf_start}} - {{school.school_hours.mttf_finish}}</span>
				</div>
				<div class="d-flex justify-content-between align-items-center w-100">
					<span class="school-hours">Wed</span>
					<span class="school-hours">{{school.school_hours.w_start}} - {{school.school_hours.w_finish}}</span>
				</div>
			</div>
			<div class="section" aria-label="office hours">
				<h3 :style="{ 'color': school.color }">Office Hours</h3>
				<div class="d-flex justify-content-between align-items-center w-100">
					<span class="school-hours">Mon - Fri</span>
					<span class="school-hours">{{school.office_hours.start}} - {{school.office_hours.finish}}</span>
				</div>
			</div>
		</div>

		<div class="grade-level-icons" aria-label="grade level icons" :style="{ 'background-color': lighterColor }">
			<cmps-preschool-icon :style="{ 'fill': school.color, 'opacity': 1}" aria-label="Preschool icon active" v-if="school.types.includes('preschool')"></cmps-preschool-icon>
			<cmps-preschool-icon aria-label="Preschool icon grayed out" v-else></cmps-preschool-icon>

			<cmps-elementary-icon :style="{ 'fill': school.color, 'opacity': 1 }" aria-label="Elementary icon active" v-if="school.types.includes('elementary')"></cmps-elementary-icon>
			<cmps-elementary-icon aria-label="Elementary icon grayed out" v-else></cmps-elementary-icon>

			<cmps-junior-high-icon :style="{ 'fill': school.color, 'opacity': 1 }" aria-label="Junior High icon active" v-if="school.types.includes('jr_high')"></cmps-junior-high-icon>
			<cmps-junior-high-icon aria-label="Junior High icon grayed out" v-else></cmps-junior-high-icon>

			<cmps-high-school-icon :style="{ 'fill': school.color, 'opacity': 1 }" aria-label="High School icon active" v-if="school.types.includes('high_school')"></cmps-high-school-icon>
			<cmps-high-school-icon aria-label="High School icon grayed out" v-else></cmps-high-school-icon>
		</div>

		<div class="footer-actions" :style="{ 'background-color': school.color, 'border-top': '1px solid white'}">
			<a
				v-if="contact_view === false"
				:class="{ 'disableClick' : school.disabled }"
				aria-label="Show school contact information"
				role="button" tabindex="0"
				@click="toggleCard('contact')"
				@keyup.enter="toggleCard('contact')">
				  Contact
			</a>
			<a
				v-else
				:style="{ 'background-color' : lightColor }"
				:class="{ 'disableClick' : school.disabled }"
				aria-label="Show school image and quote from principal"
				role="button" tabindex="0"
				@click="toggleCard('default')"
				@keyup.enter="toggleCard('default')">
				  Back
			</a>

			<router-link
				:class="{ 'disableClick' : school.disabled }"
				:aria-label="'Enrollment for ' + school.name + ' ' + school.end_name"
				:to="{ name: 'enrollment', params: { school_id: school.id }  }">
				Enroll
			</router-link>

			<a
				@click="toggleMoreModal"
				@keyup.enter="toggleMoreModal"
				:class="{ 'disableClick' : school.disabled }"
				style="border-right: none;"
				ref="moreBtn" role="button"
				aria-haspop="true" tabindex="0"
				aria-label="more information dialog">
				More
			</a>
		</div>

	</div>

</template>

<script>
	import Preschool from './shared/icons/Preschool.vue'
	import Elementary from './shared/icons/Elementary.vue'
	import JuniorHigh from './shared/icons/JuniorHigh.vue'
	import HighSchool from './shared/icons/HighSchool.vue'
	import { shadeColor } from '@/helpers/functions.js'
	import { $window } from '@/windowDimensions.js'

  export default {
		props: ['school'],
		components: {
			cmpsPreschoolIcon: Preschool,
			cmpsElementaryIcon: Elementary,
			cmpsJuniorHighIcon: JuniorHigh,
			cmpsHighSchoolIcon: HighSchool
		},
		data() {
			return {
				lighterColor: shadeColor(this.school.color, 0.7),
				lightColor: shadeColor(this.school.color, 0.5),
				default_view: true,
				contact_view: false,
			}
		},
		computed: {
      loading() { return this.$store.state.main.loading },
			addressFilterActive() { return this.$store.getters.addressSort.active && this.$router.currentRoute.name !== 'locator' },
			isMobile() { return $window.width < 769 }
		},
		mounted() {
			/*
				event is emitted in main.js whenever filteredSchools is updated
				this.toggleCard('default') causes focus errors if used here
			*/
			this.$root.$on('toggleCardDefault', () => {
				this.default_view = true
				this.contact_view = false
			})
		},
		methods: {
			triggerAnalytics(action) {
				gtag('event', action, {
          'event_category': 'card_interaction',
          'event_label': `${this.school.name}`
        })
			},
	  	toggleCompare(e) {
				let data = {
					school: this.school,
					checked: e.target.checked
				}
				this.$store.dispatch('updateCompareObject', data)
			},
	  	toggleCard(view) {
	  		switch(view) {
	  			case 'default':
	  				this.default_view = true
	  				this.contact_view = false
	  				break
	  			case 'contact':
	  				this.contact_view = true
	  				this.default_view = false
	  				setTimeout(() => this.$refs.websiteLink.focus(), 500) // need to push this later on the stack for it to work correctly (keyboard accessibility)
						break
				}
	  	},
	  	toggleMoreModal() {
	  		if (!this.school.disabled) {
	  			this.$store.dispatch('toggleMoreModal', { school: this.school, active: true, returnElement: this.$refs.moreBtn })
	  			this.triggerAnalytics('show_more_modal')
	  		}
	  	},
	  }
  }
</script>

<style scoped lang="scss">
  .isSelected {
		outline: 5px solid #39FF14;
  }
  .disable-compare {
  	opacity: 0.5;
  }
  .disableClick {
		pointer-events: none;
  }
  .addHover {
		cursor: pointer;
  }
  .card {
		font-family: 'Roboto', sans-serif;
		width: 280px;
		margin: 12px;
		display: inline-block;
		position: relative;
		&:hover {
			top: -2px;
			box-shadow: 0 12px 17px 2px rgba(0,0,0,.2),
									0 5px 22px 4px rgba(0,0,0,.2),
									0 7px 8px 0 rgba(0,0,0,.1);
		}
		a {
			color: #0000FF;
		}
	}
	.card-header {
		padding: 5px;
		height: 110px;
		overflow: hidden;
		display: flex;
		justify-content: space-around;
		align-items: center;
		&:hover {
			cursor: pointer;
		}

		h2 {
			width: 70%;
			text-align: center;
			font-size: 22px;
			font-weight: 500;
			color: #fff;
			font-family: 'Montserrat', sans-serif;
			line-height: 1.2;
		}
	}
	.compare-box label {
		padding: 5px 10px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		color: #000;
		font-weight: 500;
	}
	.content {
		font-weight: 300;
		background-color: #FFF;
		margin: 0px;
		overflow: hidden;
		transition: height .75s;
	}

	.default-view {
		height: 360px;
		// makes img height 70% of width on all screens
		.img-container {
			position: relative;
			height: 0;
			padding-bottom: 70%;
			img {
				width: 100%;
				height: 100%;
				position: absolute;
				left: 0;
				top: 0;
			}
		}
		// heights for quote container are based on 140 character limit
		.quote-container {
			background-color: ghostwhite;
			display: flex;
			flex-direction: column;
			justify-content: center;
			height: 164px;
			padding: 0px 10px;
			p {
				font-size: 0.95rem;
				margin: 0;
				// overflow-x: auto;
			}
			span {
				text-align: right;
			}
		}
	}
	.contact-view {
		display: flex;
		flex-direction: column;
		justify-content: center;
		height: 360px;
		.section {
			padding: .75rem;
			margin: 0;
			i {
				padding-right: .5rem;
			}
			a {
				word-break: break-word;
			}
		}
		hr {
			margin: .75rem;
		}
		h3 {
			font-size: 1em;
			font-weight: 500;
			margin: .25rem 0;
		}
		.school-hours {
			padding: 0 .25rem;
		}
		@media screen and (max-width: 399px) {
			.school-hours {
				font-size: .8em;
			}
		}
		@media screen and (min-width: 1216px) and (max-width: 1408px) {
			.school-hours {
				font-size: .8em;
			}
		}
	}

	@media screen and (min-width: 400px) {
		.card {
			width: 360px;
			margin: 12px;
		}
		.default-view,
		.contact-view {
			height: 416px; // 70% of width plus 164px (height of quote container)
		}
	}
	@media screen and (min-width: 1024px) {
		.card {
			width: 315px;
			margin: 10px;
		}
		.default-view,
		.contact-view {
			height: 384.5px; // 70% of width plus 164px (height of quote container)
		}
	}
	@media screen and (min-width: 1216px) {
		.card {
			width: 281px;
			margin: 8px;
		}
		.default-view,
		.contact-view {
			height: 360.7px; // 70% of width plus 164px (height of quote container)
		}
	}
	@media screen and (min-width: 1408px) {
		.card {
			width: 315px;
			margin: 12px;
		}
		.default-view,
		.contact-view {
			height: 384.5px; // 70% of width plus 164px (height of quote container)
		}
	}
	@media screen and (min-width: 1600px) {
		.card {
			width: 360px;
			margin: 12px;
		}
		.default-view,
		.contact-view {
			height: 416px; // 70% of width plus 164px (height of quote container)
		}
	}
	@media screen and (min-width: 1803px) {
		.card {
			width: 328px;
			margin: 12px;
		}
		.default-view,
		.contact-view {
			height: 393.6px; // 70% of width plus 164px (height of quote container)
		}
	}
	@media screen and (min-width: 1931px) {
		.card {
			width: 360px;
			margin: 12px;
		}
		.default-view,
		.contact-view {
			height: 416px; // 70% of width plus 164px (height of quote container)
		}
	}
	// for locator page, so cards fit nicely
	@media screen and (min-width: 1024px) {
		.card.locator {
			width: 300px;
			margin: 10px;
			.default-view,
			.contact-view {
				height: 374px; // 70% of width plus 164px (height of quote container)
			}
		}
	}
	// for locator page, so cards fit nicely
	@media screen and (min-width: 1216px) {
		.card.locator {
			width: 360px;
			margin: 12px;
			.default-view,
			.contact-view {
				height: 416px; // 70% of width plus 164px (height of quote container)
			}
		}
	}
	.grade-level-icons {
		display: flex;
    justify-content: center;
    padding: 10px 0px;
		svg {
	    width: 28px;
	    height: 28px;
	    margin: 0px 8px;
	    fill: #a8a8a8;
	    opacity: 0.6;
		}
	}
	.footer-actions {
	  display: flex;
	  align-items: center;
	  justify-content: space-between;
		a {
			color: #fff;
			padding: 10px;
			font-size: 18px;
			transition: color 0.3s ease;
			text-decoration: none;
			width: 33.33333%;
			text-align: center;
			border-right: 1px solid #fff;
		}
		a:hover {
			opacity: 0.8;
		}
	}
</style>
